import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown, Save, Repeat, Eye, EyeOff, ImagePlus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export const SITE_IMAGE_SLOTS = [
  { key: "hero", label: "Startseite · Hero-Hintergrundbild" },
  { key: "band", label: "Startseite · Bild-Banner (Nacht)" },
  { key: "band-2", label: "Startseite · Bild-Banner (Tag)" },
  { key: "referenz", label: "Referenzen" },
  { key: "app-screenshot", label: "DROPS Connect · App-Screenshot" },
  { key: "seo-bauwerksinspektion", label: "Seite · Bauwerksinspektion" },
  { key: "seo-brueckeninspektion", label: "Seite · Brückeninspektion" },
  { key: "seo-fassadeninspektion", label: "Seite · Fassadeninspektion" },
  { key: "seo-stuetzwaende-hangsicherung", label: "Seite · Stützwände/Hangsicherung" },
  { key: "seo-industrieinspektion", label: "Seite · Industrieinspektion" },
  { key: "seo-bahninfrastruktur", label: "Seite · Bahn/Infrastruktur" },
  { key: "seo-360-grad-dokumentation", label: "Seite · 360°-Dokumentation" },
  { key: "seo-photogrammetrie-3d", label: "Seite · Photogrammetrie & 3D" },
  { key: "seo-rtk-vermessung", label: "Seite · RTK-Vermessung" },
  { key: "seo-technical-first-responder", label: "Seite · Technical First Responder" },
];

const field = "mt-2 w-full border-b border-white/20 bg-transparent py-2 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none";
const lbl = "font-tac text-[10px] uppercase tracking-[0.25em] text-white/40";

export default function SiteImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadSlot, setUploadSlot] = useState("hero");
  const [form, setForm] = useState({ title: "", alt: "", description: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const replaceRefs = useRef({});

  const load = () => {
    setLoading(true);
    axios.get(`${API}/site-images`, { withCredentials: true })
      .then((r) => setImages(r.data)).catch(() => toast.error("Bilder konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Bitte eine Bilddatei wählen"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("slot", uploadSlot);
      fd.append("title", form.title);
      fd.append("alt", form.alt);
      fd.append("description", form.description);
      fd.append("file", f);
      await axios.post(`${API}/site-images`, fd, { withCredentials: true });
      setForm({ title: "", alt: "", description: "" });
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Bild hochgeladen");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const saveMeta = async (img) => {
    try {
      await axios.put(`${API}/site-images/${img.id}`, { title: img.title, alt: img.alt, description: img.description, active: img.active }, { withCredentials: true });
      toast.success("Gespeichert");
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };
  const toggleActive = async (img) => {
    const next = !img.active;
    setImages((xs) => xs.map((x) => x.id === img.id ? { ...x, active: next } : x));
    try { await axios.put(`${API}/site-images/${img.id}`, { title: img.title, alt: img.alt, description: img.description, active: next }, { withCredentials: true }); }
    catch { toast.error("Fehler"); load(); }
  };
  const replace = async (img, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await axios.post(`${API}/site-images/${img.id}/replace`, fd, { withCredentials: true });
      toast.success("Bild ersetzt"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Ersetzen fehlgeschlagen"); }
  };
  const remove = async (img) => {
    if (!window.confirm("Dieses Bild wirklich löschen?")) return;
    try { await axios.delete(`${API}/site-images/${img.id}`, { withCredentials: true }); toast.success("Gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };
  const move = async (slot, list, index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const ordered = [...list];
    [ordered[index], ordered[j]] = [ordered[j], ordered[index]];
    setImages((xs) => xs.filter((x) => x.slot !== slot).concat(ordered.map((o, i) => ({ ...o, sort_order: i }))));
    try { await axios.post(`${API}/site-images/reorder`, { slot, ordered_ids: ordered.map((o) => o.id) }, { withCredentials: true }); }
    catch { toast.error("Sortierung fehlgeschlagen"); load(); }
  };

  const setImgField = (id, key, val) => setImages((xs) => xs.map((x) => x.id === id ? { ...x, [key]: val } : x));

  const slots = [...new Set(images.map((i) => i.slot))].sort();
  const slotLabel = (k) => SITE_IMAGE_SLOTS.find((s) => s.key === k)?.label || k;

  return (
    <div className="mt-10" data-testid="admin-site-images-section">
      <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">WEBSITE-BILDER</p>
      <h2 className="mt-4 font-display text-3xl font-black uppercase tracking-tighter">Bildverwaltung</h2>
      <p className="mt-3 max-w-2xl font-tac text-xs leading-relaxed text-[#A1A1AA]">
        Lade Bilder für die Website hoch, ersetze sie, sortiere sie und pflege Titel &amp; Alt-Text — ganz ohne Code.
        Jeder Bereich (Slot) erscheint an der zugeordneten Stelle der Website.
      </p>

      {/* Upload */}
      <form onSubmit={upload} className="mt-8 border border-white/10 bg-white/[0.02] p-6" data-testid="site-image-upload-form">
        <p className="flex items-center gap-2 font-tac text-[11px] tracking-[0.25em] text-white/70"><ImagePlus className="h-4 w-4 text-[#F5B301]" /> NEUES BILD</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <label className="block"><span className={lbl}>Bereich</span>
            <select data-testid="site-image-slot" value={uploadSlot} onChange={(e) => setUploadSlot(e.target.value)} className={`${field} [&>option]:bg-[#09090B]`}>
              {SITE_IMAGE_SLOTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="block"><span className={lbl}>Titel</span>
            <input data-testid="site-image-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} placeholder="z. B. Brückenunterseite" /></label>
          <label className="block"><span className={lbl}>Alt-Text (SEO)</span>
            <input data-testid="site-image-alt" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} className={field} placeholder="z. B. Drohnenaufnahme einer Brückenunterseite" /></label>
          <label className="block"><span className={lbl}>Beschreibung</span>
            <input data-testid="site-image-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} /></label>
          <label className="block sm:col-span-2"><span className={lbl}>Bilddatei</span>
            <input ref={fileRef} data-testid="site-image-file" type="file" accept="image/*" className="mt-2 w-full font-tac text-xs text-white/60 file:mr-4 file:border file:border-[#F5B301] file:bg-transparent file:px-4 file:py-2 file:font-tac file:text-[10px] file:tracking-[0.2em] file:text-[#F5B301]" /></label>
        </div>
        <button data-testid="site-image-upload-submit" disabled={uploading} className="mt-6 flex items-center gap-2 bg-[#F5B301] px-8 py-3 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} HOCHLADEN
        </button>
      </form>

      {/* List */}
      {loading ? (
        <p className="mt-10 font-tac text-xs text-white/40">Lädt…</p>
      ) : slots.length === 0 ? (
        <p className="mt-10 font-tac text-xs text-white/40">Noch keine Bilder hochgeladen.</p>
      ) : slots.map((slot) => {
        const list = images.filter((i) => i.slot === slot).sort((a, b) => a.sort_order - b.sort_order);
        return (
          <div key={slot} className="mt-10" data-testid={`site-image-slot-group-${slot}`}>
            <h3 className="font-tac text-[11px] tracking-[0.25em] text-[#F5B301]">{slotLabel(slot).toUpperCase()} <span className="text-white/30">· {list.length}</span></h3>
            <div className="mt-4 space-y-4">
              {list.map((img, i) => (
                <div key={img.id} data-testid={`site-image-${img.id}`} className={`flex flex-col gap-4 border border-white/10 p-4 sm:flex-row ${img.active ? "" : "opacity-50"}`}>
                  <img src={`${BACKEND}${img.url}`} alt={img.alt} className="h-28 w-40 shrink-0 border border-white/10 object-cover" />
                  <div className="flex-1 space-y-3">
                    <label className="block"><span className={lbl}>Titel</span>
                      <input data-testid={`site-image-title-${img.id}`} value={img.title} onChange={(e) => setImgField(img.id, "title", e.target.value)} className={field} /></label>
                    <label className="block"><span className={lbl}>Alt-Text</span>
                      <input data-testid={`site-image-alt-${img.id}`} value={img.alt} onChange={(e) => setImgField(img.id, "alt", e.target.value)} className={field} /></label>
                    <label className="block"><span className={lbl}>Beschreibung</span>
                      <input value={img.description} onChange={(e) => setImgField(img.id, "description", e.target.value)} className={field} /></label>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button onClick={() => saveMeta(img)} data-testid={`site-image-save-${img.id}`} className="flex items-center gap-1 border border-white/20 px-3 py-1.5 font-tac text-[9px] tracking-[0.15em] text-white/70 hover:border-[#F5B301] hover:text-[#F5B301]"><Save className="h-3 w-3" /> SPEICHERN</button>
                      <button onClick={() => toggleActive(img)} data-testid={`site-image-toggle-${img.id}`} className="flex items-center gap-1 border border-white/20 px-3 py-1.5 font-tac text-[9px] tracking-[0.15em] text-white/70 hover:border-[#F5B301] hover:text-[#F5B301]">{img.active ? <><Eye className="h-3 w-3" /> SICHTBAR</> : <><EyeOff className="h-3 w-3" /> VERBORGEN</>}</button>
                      <input ref={(el) => (replaceRefs.current[img.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => replace(img, e.target.files[0])} />
                      <button onClick={() => replaceRefs.current[img.id]?.click()} data-testid={`site-image-replace-${img.id}`} className="flex items-center gap-1 border border-white/20 px-3 py-1.5 font-tac text-[9px] tracking-[0.15em] text-white/70 hover:border-[#F5B301] hover:text-[#F5B301]"><Repeat className="h-3 w-3" /> ERSETZEN</button>
                      <button onClick={() => move(slot, list, i, -1)} disabled={i === 0} data-testid={`site-image-up-${img.id}`} className="border border-white/20 p-1.5 text-white/60 hover:text-[#F5B301] disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                      <button onClick={() => move(slot, list, i, 1)} disabled={i === list.length - 1} data-testid={`site-image-down-${img.id}`} className="border border-white/20 p-1.5 text-white/60 hover:text-[#F5B301] disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
                      <button onClick={() => remove(img)} data-testid={`site-image-delete-${img.id}`} className="ml-auto flex items-center gap-1 text-white/40 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
